import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useBranchFirst from '../../hooks/useBranchFirst';
import {
  FiX, FiClipboard, FiUserPlus, FiTruck, FiFileText,
} from 'react-icons/fi';

/**
 * Phone "quick create" bottom sheet, opened from the bottom-nav center (+) FAB.
 * Routes to each create flow. Portaled to <body> so it overlays everything and
 * isn't clipped by a transformed ancestor.
 *
 * "New invoice" picks the branch first (All mode); the dialog is rendered even
 * after the sheet closes, since Layout keeps this component mounted.
 */
export default function QuickCreateMenu({ open, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pickBranch, branchDialog } = useBranchFirst();
  if (!open) return branchDialog;

  const go = (path, state) => {
    onClose();
    navigate(path, state ? { state } : undefined);
  };

  const items = [
    {
      key: 'jobCard',
      icon: FiClipboard,
      label: t('qc.newJobCard'),
      onClick: () => go('/job-cards', { quickCreate: true }),
    },
    {
      key: 'customer',
      icon: FiUserPlus,
      label: t('qc.newCustomer'),
      onClick: () => go('/customers', { quickCreate: true }),
    },
    {
      key: 'vehicle',
      icon: FiTruck,
      label: t('qc.newVehicle'),
      onClick: () => go('/vehicles', { quickCreate: true }),
    },
    {
      key: 'invoice',
      icon: FiFileText,
      label: t('qc.newInvoice'),
      onClick: async () => {
        onClose();
        const id = await pickBranch();
        if (!id) return;
        navigate('/invoices/create', { state: { branchId: id } });
      },
    },
  ];

  return createPortal(
    <>
      {branchDialog}
      <div className="fixed inset-0 z-[55] md:hidden" role="dialog" aria-modal="true">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
        <div className="absolute inset-x-0 bottom-0 bg-surface border-t border-edge rounded-t-2xl p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-2xl">
          <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-edge-strong" aria-hidden="true" />
          <div className="flex items-center justify-between px-2 pb-1">
            <span className="text-sm font-semibold text-content">{t('qc.title')}</span>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('cm.close')}
              className="p-2 -m-1 rounded-lg text-content-muted hover:text-content hover:bg-surface-2 transition-colors"
            >
              <FiX size={18} />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-1">
            {items.map((it) => {
              const Icon = it.icon;
              return (
                <button
                  key={it.key}
                  type="button"
                  onClick={it.onClick}
                  className="flex items-center gap-3 w-full px-3 py-3 rounded-xl text-start hover:bg-surface-2 active:bg-surface-3 transition-colors"
                >
                  <span className="w-10 h-10 rounded-xl bg-accent-soft text-accent flex items-center justify-center shrink-0">
                    <Icon size={20} />
                  </span>
                  <span className="text-sm font-medium text-content">{it.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
